import React, { useEffect, useMemo, useState } from "react";
import TitleSection from "../../components/TitleSection";
import { Button, Card, Divider, Form, Select, Space, Typography, message } from "antd";
import { generateFormRules, handleSubmitFailed } from "../../helpers/formRules";
import { RightCircleOutlined } from "@ant-design/icons";
import useAuth from "../../hooks/useAuth";
import { GroupProperties } from "../../types/group.type";
import { getErrorMessage } from "../../helpers/errorHandler";
import { httpRequest } from "../../helpers/api";
import { EGroupMemberStatus, GroupMemberProperties } from "../../types/groupMember.type";
import MemberInformation from "./Components/MemberInformation";
import { useHistory } from "react-router-dom";
import { successModal } from "../../helpers/modal";
import { generateQueryString } from "../../helpers/generateQueryString";
import { EFeePosition, EFeeStatus } from "../../types/masterFee.type";
import { generateNextFeeCharged } from "../../helpers/fee";
import moment from "moment";
import { ListItem } from "../group/CreateComponents/GroupInformation";
import { UserProperties } from "../../types/user.type";
import { EmployeeProperties } from "../../types/employee.type";
import { ProfileProperties } from "../../types/profile.type";
import { BaseResponsePaginationProps } from "../../types/config.type";

export interface TransferFormValues {
	originGroupId?: string;
	originUserId?: string;
	toGroupId?: string;
	originGroupMemberId?: string;
}

const { Title } = Typography;

export default function CreateTransferMember() {
	const [form] = Form.useForm();
	const history = useHistory();
	const { officeIdList } = useAuth();
	const [groups, setGroups] = useState<GroupProperties[]>([]);
	const [groupMembers, setGroupMembers] = useState<GroupMemberProperties[]>([]);
	const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
	const [selectedMember, setSelectedMember] = useState<ListItem | undefined>();

	const originGroupIdWatch = Form.useWatch("originGroupId", form);
	const originGroupMemberIdWatch = Form.useWatch("originGroupMemberId", form);
	const toGroupIdWatch = Form.useWatch("toGroupId", form);

	useEffect(() => {
		(async () => {
			try {
				const res = await httpRequest.get<BaseResponsePaginationProps<GroupProperties>>(
					`/groups?filterOfficeId=${officeIdList}&limit=9999`
				);

				setGroups(res.data.payload.results);
			} catch (error) {
				message.error(getErrorMessage(error));
			}
		})();
	}, [officeIdList]);

	useEffect(() => {
		if (originGroupIdWatch) {
			(async () => {
				try {
					const params = {
						filterGroupId: originGroupIdWatch,
						limit: 9999,
						filterStatus: EGroupMemberStatus.ACTIVE,
						includeData: "user,userListingCount,employee,teamsMemberFees",
						withLeaderTeam: "false",
					};
					const res = await httpRequest.get<BaseResponsePaginationProps<GroupMemberProperties>>(
						`group-members${generateQueryString(params)}`
					);

					setGroupMembers(res.data.payload.results);
				} catch (error) {
					message.error(getErrorMessage(error));
				}
			})();
		}
	}, [originGroupIdWatch]);

	const isDisableSubmit = useMemo(() => {
		return !originGroupIdWatch || !originGroupMemberIdWatch || !toGroupIdWatch;
	}, [originGroupIdWatch, originGroupMemberIdWatch, toGroupIdWatch]);

	const _handleSubmit = async (values: TransferFormValues) => {
		if (!selectedMember) return message.error("Please select a member first.");
		try {
			setIsActionLoading(true);

			const data = {
				...values,
				userId: selectedMember?.user.userId,
			};

			const params = {
				filterGroupId: data.toGroupId,
				filterUserId: data.userId,
				filterStatus: EGroupMemberStatus.ACTIVE,
			};
			const check = await httpRequest.get<BaseResponsePaginationProps<GroupMemberProperties>>(
				`group-members${generateQueryString(params)}`
			);
			if (check.data.payload.results.length > 0) {
				message.error(
					getErrorMessage(`User already exists in group ${check.data.payload.results[0].group?.groupName}`)
				);
				return;
			}

			// const newFees = selectedMember.user.teamsMemberFees?.filter((row) => row.isNew);

			// let updatedFeeIds = selectedMember?.user.teamsMemberFees
			// 	?.filter((item) => item.teamsMemberFeeItemId)
			// 	.map((item) => item.teamsMemberFeeItemId);
			// let deletedFees = (selectedMember?.user?.deletedFees ?? [])!.filter(
			// 	(item) => !updatedFeeIds?.includes(item.teamsMemberFeeItemId)
			// );

			const res: any = await httpRequest.post("transfer-member-teams", data);

			if (res.data.payload.toGroupMemberId) {
				const dtoFee = {
					groupMemberId: res.data.payload.toGroupMemberId,
					userId: selectedMember.user.userId,
					employeeId: selectedMember.user?.employee?.[0].employeeId,
					teamsMemberFeeItems: (selectedMember.user.teamsMemberFees ?? []).map((fee) => ({
						feeFor: fee.feeFor,
						type: fee.type,
						value: fee.value,
						frequency: fee.frequency,
						nextChargeAt: fee.nextChargeAt,
						startAt: fee.startAt,
						endAt: fee.endAt,
					})),
				};

				await httpRequest.post("teams-member-fees", dtoFee);

				// let promises: Promise<unknown>[] = [];

				// for (const fee of newFees ?? []) {
				// 	let dtoFee = {
				// 		teamsMemberFeeId: selectedMember?.user?.teamsMemberFeeId,
				// 		position: EFeePosition.BOTTOM,
				// 		type: fee.type,
				// 		frequency: fee.frequency,
				// 		feeFor: fee.feeFor,
				// 		value: fee.value,
				// 		nextChargeAt: fee.nextChargeAt,
				// 		startAt: fee.startAt,
				// 		endAt: fee.endAt,
				// 		status: EFeeStatus.PUBLISH,
				// 	};
				// 	promises.push(httpRequest.post("teams-member-fee-items", dtoFee));
				// }

				// for (const fee of deletedFees) {
				// 	promises.push(httpRequest.delete(`teams-member-fee-items/${fee.teamsMemberFeeItemId}`));
				// }

				// if (selectedMember?.user?.teamsMemberFeeId) {
				// 	promises.push(
				// 		httpRequest.patch(`teams-member-fees/${selectedMember?.user?.teamsMemberFeeId}`, {
				// 			groupMemberId: res.data.payload.toGroupMemberId,
				// 		})
				// 	);
				// }

				// await Promise.all(promises);
			}

			setSelectedMember(undefined);
			form.resetFields();
			successModal({
				title: "Success!",
				subtitle: "Transfer created!",
				onOk: () => {
					history.push("/transfer-team-member");
				},
			});
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsActionLoading(false);
		}
	};

	useEffect(() => {
		if (originGroupMemberIdWatch) {
			const selected = groupMembers.find((row) => row.groupMemberId === originGroupMemberIdWatch);
			let formatted = {
				groupMemberId: selected?.groupMemberId,
				user: {
					...(selected?.user as UserProperties),
					employee: [
						{
							...(selected?.employee as EmployeeProperties),
						},
					],
					profile: {
						...(selected?.profile as ProfileProperties),
					},
					teamsMemberFees:
						selected?.teamsMemberFees &&
						selected?.teamsMemberFees[0]?.teamsMemberFeeItems!.map((item) => ({
							...item,
							isNew: false,
						})),
					teamsMemberFeeId: selected?.teamsMemberFees?.[0]?.teamsMemberFeeId,
				},
			};
			setSelectedMember(formatted);
		}
	}, [groupMembers, originGroupMemberIdWatch]);

	return (
		<React.Fragment>
			<TitleSection
				icon="back"
				title="Transfer Team Member"
				rightAction={
					<Button
						onClick={form.submit}
						type="primary"
						disabled={isDisableSubmit}
						loading={isActionLoading}
					>
						Transfer Now
					</Button>
				}
			/>
			<Form
				form={form}
				layout="vertical"
				onFinish={_handleSubmit}
				onFinishFailed={handleSubmitFailed}
			>
				<Space
					direction="vertical"
					size="large"
				>
					<Card
						bordered={false}
						size="small"
					>
						<div className="transfer-grid">
							<div>
								<Title level={3}>From Group</Title>

								<Form.Item
									label="Select Group"
									name="originGroupId"
									rules={generateFormRules("From Group", ["required"])}
								>
									<Select
										virtual={true}
										showSearch
										style={{ width: "100%" }}
										placeholder="Select group"
										optionFilterProp="children"
										filterOption={(input, option) =>
											(option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
										}
										options={groups.map((value) => ({
											label: value.groupName,
											value: value.groupId,
										}))}
										onChange={() => {
											form.setFieldValue("originGroupMemberId", undefined);
											form.setFieldValue("toGroupId", undefined);
										}}
									/>
								</Form.Item>

								<Form.Item
									label="Select Member"
									name="originGroupMemberId"
									rules={generateFormRules("Member", ["required"])}
								>
									<Select
										virtual={true}
										showSearch
										style={{ width: "100%" }}
										placeholder="Select member"
										optionFilterProp="children"
										filterOption={(input, option) =>
											(option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
										}
										options={groupMembers.map((value) => ({
											label: value.user?.nickName?.toUpperCase(),
											value: value.groupMemberId,
										}))}
									/>
								</Form.Item>

								<MemberInformation
									member={selectedMember}
									setSelectedMember={setSelectedMember}
								/>
							</div>
							<div>
								<div className="transfer-separator">
									<Divider
										type="vertical"
										style={{ height: "100%", margin: "auto" }}
									/>
									<RightCircleOutlined style={{ fontSize: 30, color: "#20A1F5", margin: "auto" }} />
									<Divider
										type="vertical"
										style={{ height: "100%", margin: "auto" }}
									/>
								</div>
							</div>
							<div>
								<Title level={3}>To Group</Title>

								<Form.Item
									label="Select Group"
									name="toGroupId"
									rules={generateFormRules("To Group", ["required"])}
								>
									<Select
										virtual={true}
										showSearch
										style={{ width: "100%" }}
										placeholder="Select group"
										optionFilterProp="children"
										filterOption={(input, option) =>
											(option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
										}
										options={groups
											.filter((row) => row.groupId !== originGroupIdWatch)
											.map((value) => ({
												label: value.groupName,
												value: value.groupId,
											}))}
									/>
								</Form.Item>
							</div>
						</div>
					</Card>
				</Space>
			</Form>
		</React.Fragment>
	);
}
